import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Mail, Lock, Eye, EyeOff, KeyRound, ShieldAlert } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import "../components/landing/arc/arc.css";

const inputClass =
  "arc-body w-full pl-11 pr-4 py-3 rounded-xl bg-white border border-black/[0.1] text-[#141414] text-sm font-medium placeholder-[#141414]/35 focus:border-[#141414]/40 focus:outline-none focus:ring-1 focus:ring-[#141414]/20 transition-colors";

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, googleLogin, resetTempPassword } = useAuth();
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [requiresOtp, setRequiresOtp] = useState(false);
  const [error, setError] = useState(location.state?.error || "");

  // Check for Google OAuth error in URL params
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const googleError = params.get('error');
    const googleEmail = params.get('google_email');
    if (googleError === 'no_account') {
      const msg = googleEmail
        ? `No Voxinity account is linked to "${googleEmail}". Please sign up with this email first, or use a different Google account.`
        : 'No account found for this Google email. Please sign up first.';
      setError(msg);
      toast.error('No account found. Please sign up first.');
      window.history.replaceState(null, '', '/login');
    } else if (googleError === 'token_exchange_failed' || googleError === 'userinfo_failed' || googleError === 'oauth_error') {
      setError('Google sign-in failed. Please try again or use email/password.');
      toast.error('Google sign-in failed');
      window.history.replaceState(null, '', '/login');
    }
  }, []);
  
  // Password reset state
  const [needsPasswordReset, setNeedsPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Where to land after a successful login. Two precedence sources:
  //   1. `?next=<encoded path>` query string — used by external deep-link
  //      flows (e.g. GHL Custom Menu Link click that bounces here when the
  //      user isn't authenticated). Only same-origin paths are honoured.
  //   2. `location.state.from.pathname` — set by React-Router's internal
  //      redirect-after-auth pattern (e.g. PrivateRoute redirects).
  //   3. Default fallback: /dashboard.
  const nextParam = new URLSearchParams(location.search).get("next");
  const safeNext = (nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//"))
    ? nextParam
    : null;
  const from = safeNext || location.state?.from?.pathname || "/dashboard";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const result = await login(email, password, requiresOtp ? otpCode : null);
      
      if (result.funnelOnboarding) {
        toast.info("Finish setting up your Challenge account");
        navigate(`/challenge/vip?resume=${encodeURIComponent(result.email)}`);
        return;
      }

      if (result.requiresOtp) {
        setRequiresOtp(true);
        toast.success("OTP sent to your email");
      } else if (result.needsPasswordReset) {
        setNeedsPasswordReset(true);
        setRequiresOtp(false);
        toast.info("Please set a new password to continue");
      } else {
        toast.success("Welcome back!");
        navigate(from, { replace: true });
      }
    } catch (err) {
      const message = err.response?.data?.detail || "Login failed. Please try again.";
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      setError("Passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (newPassword === password) {
      setError("New password must be different from temporary password");
      return;
    }
    
    setIsLoading(true);
    setError("");
    
    try {
      await resetTempPassword(email, password, newPassword);
      toast.success("Password updated! Welcome to Voxinity AI");
      navigate(from, { replace: true });
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to reset password";
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    googleLogin();
  };

  return (
    <div className="min-h-screen arc-bone flex items-center justify-center px-4 py-12 relative overflow-hidden" data-testid="login-page">
      {/* Background blobs */}
      <div className="arc-blob w-96 h-96 bg-[#D8D2C8]/70 -top-24 -left-24" />
      <div className="arc-blob w-80 h-80 bg-[#E0DACE]/80 bottom-0 right-0" />

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <Link to="/" className="inline-block">
            <img src="/voxinity-logo-light.png?v=8" alt="Voxinity AI" className="h-10 w-auto" />
          </Link>
        </motion.div>

        {/* Login Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="rounded-2xl border border-black/[0.08] bg-white shadow-[0_8px_40px_rgba(20,20,20,0.1)] p-6 md:p-8"
        >
          <div className="text-center mb-6">
            <h1 className="arc-head font-black text-2xl text-[#141414] mb-2">
              {needsPasswordReset ? "Set Your New Password" : requiresOtp ? "Enter Verification Code" : "Welcome back"}
            </h1>
            <p className="arc-body text-sm text-[#141414]/55">
              {needsPasswordReset
                ? "You're using a temporary password. Please set a new one to continue."
                : requiresOtp 
                  ? "We sent a 6-digit code to your email"
                  : "Sign in to your account to continue"
              }
            </p>
          </div>

          {error && (
            <div className="arc-body mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm font-medium">
              {error}
            </div>
          )}

          {needsPasswordReset ? (
            /* Password Reset Form */
            <form onSubmit={handleResetPassword} className="space-y-4" data-testid="password-reset-form">
              <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3 mb-2">
                <ShieldAlert size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="arc-body text-sm text-amber-800">
                  For security, please create a strong password with at least 8 characters.
                </p>
              </div>
              
              <div>
                <label className="arc-body block text-sm font-bold text-[#141414]/70 mb-1.5">New Password</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#141414]/35" />
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Min. 8 characters"
                    className={`${inputClass} pr-12`}
                    required
                    minLength={8}
                    data-testid="reset-new-password-input"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#141414]/35 hover:text-[#141414]/70"
                  >
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              
              <div>
                <label className="arc-body block text-sm font-bold text-[#141414]/70 mb-1.5">Confirm New Password</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#141414]/35" />
                  <input
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className={inputClass}
                    required
                    minLength={8}
                    data-testid="reset-confirm-password-input"
                  />
                </div>
              </div>
              
              <button
                type="submit"
                disabled={isLoading || !newPassword || !confirmNewPassword}
                className="arc-body group w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#141414] text-[#F0EFEC] font-bold hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="reset-password-submit-btn"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                ) : (
                  <>
                    Set Password & Continue
                    <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>
          ) : !requiresOtp && (
            <>
              {/* Google Sign In */}
              <button
                onClick={handleGoogleLogin}
                className="arc-body w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl bg-white border border-black/[0.12] text-[#141414] font-medium hover:bg-black/[0.03] transition-colors duration-200 mb-6"
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </button>

              {/* Divider */}
              <div className="flex items-center gap-4 mb-6">
                <div className="flex-1 h-px bg-black/[0.08]" />
                <span className="arc-body text-xs text-[#141414]/45">or continue with email</span>
                <div className="flex-1 h-px bg-black/[0.08]" />
              </div>
            </>
          )}

          {/* Form */}
          {!needsPasswordReset && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {!requiresOtp ? (
              <>
                <div>
                  <label className="arc-body block text-sm font-bold text-[#141414]/70 mb-1.5">Email</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#141414]/35" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className={inputClass}
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="arc-body block text-sm font-bold text-[#141414]/70 mb-1.5">Password</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#141414]/35" />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className={`${inputClass} pr-12`}
                      required
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#141414]/35 hover:text-[#141414]/70"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="rounded border-black/25 bg-white text-[#141414] focus:ring-[#141414]/30" />
                    <span className="arc-body text-[#141414]/60">Remember me</span>
                  </label>
                  <Link to="/forgot-password" className="arc-body font-bold text-[#141414] underline underline-offset-2 hover:text-black">
                    Forgot password?
                  </Link>
                </div>
              </>
            ) : (
              <div>
                <label className="arc-body block text-sm font-bold text-[#141414]/70 mb-1.5">Verification Code</label>
                <div className="relative">
                  <KeyRound size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#141414]/35" />
                  <input
                    type="text"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    className={`${inputClass} text-center tracking-[0.3em] font-mono`}
                    required
                    maxLength={6}
                    data-testid="login-otp-input"
                    autoFocus
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setRequiresOtp(false);
                    setOtpCode("");
                  }}
                  className="arc-body mt-2 text-sm text-[#141414]/55 hover:text-[#141414]"
                >
                  ← Back to login
                </button>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || (requiresOtp && otpCode.length !== 6)}
              className="arc-body group w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#141414] text-[#F0EFEC] font-bold hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="login-submit-btn"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-current/30 border-t-current rounded-full animate-spin" />
              ) : (
                <>
                  {requiresOtp ? "Verify & Sign In" : "Sign In"}
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </>
              )}
            </button>
          </form>
          )}

          {/* Sign up link */}
          <p className="arc-body text-center text-sm text-[#141414]/55 mt-6">
            Don't have an account?{" "}
            <Link to="/signup" className="font-bold text-[#141414] underline underline-offset-2">
              Get started
            </Link>
          </p>
        </motion.div>

        {/* Footer */}
        <p className="arc-body text-center text-xs text-[#141414]/40 mt-8">
          © 2026 Voxinity AI. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
